import React from "react";
import { ClipboardPaste, Link2, Upload, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useIngestions } from "@/hooks/useIngestions";
import { formatDistanceToNow } from "date-fns";
import type { RawIngestion, SourceType, IngestionStatus, IngestionDataType } from "./types";

interface IngestionHistoryListProps {
  onSelect: (ingestion: RawIngestion) => void;
  selectedId?: string | null;
}

const sourceIcons: Record<SourceType, React.ReactNode> = {
  paste: <ClipboardPaste className="w-3.5 h-3.5" />,
  link: <Link2 className="w-3.5 h-3.5" />,
  upload: <Upload className="w-3.5 h-3.5" />,
};

const statusColors: Record<IngestionStatus, string> = {
  captured: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  pending: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30",
  processing: "bg-purple-500/20 text-purple-300 border-purple-500/30",
  review: "bg-orange-500/20 text-orange-300 border-orange-500/30",
  processed: "bg-green-500/20 text-green-300 border-green-500/30",
  failed: "bg-red-500/20 text-red-300 border-red-500/30",
};

const dataTypeLabels: Record<IngestionDataType, string> = {
  provider: "Provider",
  program: "Program",
  schedule: "Schedule",
  calendar: "Calendar",
  category: "Category",
  other: "Other",
};

const IngestionHistoryList: React.FC<IngestionHistoryListProps> = ({
  onSelect,
  selectedId,
}) => {
  const { data: ingestions, isLoading, error } = useIngestions();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-white/50">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading history...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-400 text-sm py-4">
        Failed to load ingestion history
      </div>
    );
  }

  if (!ingestions || ingestions.length === 0) {
    return (
      <div className="text-white/50 text-sm py-8 text-center">
        No ingestions yet. Capture content using the tabs above.
      </div>
    );
  }

  return (
    <ScrollArea className="h-[300px]">
      <div className="space-y-2 pr-2">
        {ingestions.map((ingestion) => (
          <button
            key={ingestion.id}
            onClick={() => onSelect(ingestion)}
            className={`w-full text-left p-3 rounded-lg border transition-colors ${
              selectedId === ingestion.id
                ? "bg-white/20 border-white/40"
                : "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"
            }`}
          >
            <div className="flex items-start gap-2">
              {/* Source icon */}
              <div className="text-white/60 mt-0.5">
                {sourceIcons[ingestion.source_type]}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                {/* Source identifier */}
                <div className="text-white text-sm font-medium truncate">
                  {ingestion.source_type === "link" && ingestion.source_url
                    ? new URL(ingestion.source_url).hostname
                    : ingestion.source_type === "upload" && ingestion.source_file_name
                    ? ingestion.source_file_name
                    : ingestion.source_type === "paste"
                    ? `Pasted content (${ingestion.raw_content.length} chars)`
                    : "Unknown source"}
                </div>

                {/* Badges row */}
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  <Badge
                    variant="outline"
                    className={`text-[10px] px-1.5 py-0 ${statusColors[ingestion.status]}`}
                  >
                    {ingestion.status}
                  </Badge>
                  {ingestion.data_type && (
                    <Badge
                      variant="outline"
                      className="text-[10px] px-1.5 py-0 bg-white/10 text-white/70 border-white/20"
                    >
                      {dataTypeLabels[ingestion.data_type]}
                    </Badge>
                  )}
                  {ingestion.review_status && (
                    <Badge
                      variant="outline"
                      className={`text-[10px] px-1.5 py-0 ${
                        ingestion.review_status === "approved"
                          ? "bg-green-500/20 text-green-300 border-green-500/30"
                          : ingestion.review_status === "rejected"
                          ? "bg-red-500/20 text-red-300 border-red-500/30"
                          : "bg-yellow-500/20 text-yellow-300 border-yellow-500/30"
                      }`}
                    >
                      {ingestion.review_status}
                    </Badge>
                  )}
                </div>

                {/* Timestamp */}
                <div className="text-white/40 text-xs mt-1.5">
                  {formatDistanceToNow(new Date(ingestion.created_at), {
                    addSuffix: true,
                  })}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>
    </ScrollArea>
  );
};

export default IngestionHistoryList;
